// Deep links to one field: "/project/asset?tab=pv&pv=losses#field-pv-loss-soiling".
//
// A link from an analysis (the PV sizing's read-only assumptions, say) lands
// on the page AND on the field: the element whose id is the URL hash is
// scrolled into view, its input focused and briefly highlighted. Fields inside
// a tab or a collapsed section may appear a moment after the page, so the
// element is awaited for a short while rather than looked up once. Pages opt
// in simply by giving the wrapper of a field the id; the registry of ids and
// routes is pages/feasibility/projectFieldLinks.js.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const FIELD_FOCUS_PREFIX = "field-";
const WAIT_MS = 2500;
const POLL_MS = 100;
const HIGHLIGHT_MS = 2200;
const FOCUSABLE = "input:not([type=hidden]), select, textarea, [role=combobox], [role=switch], button";

export function focusField(id) {
  const element = typeof document !== "undefined" ? document.getElementById(id) : null;
  if (!element) return false;
  try {
    element.scrollIntoView({ block: "center", behavior: "smooth" });
  } catch {
    element.scrollIntoView();
  }
  const input = element.matches(FOCUSABLE) ? element : element.querySelector(FOCUSABLE);
  if (input && typeof input.focus === "function") {
    try { input.focus({ preventScroll: true }); } catch { input.focus(); }
  }
  const previous = { outline: element.style.outline, outlineOffset: element.style.outlineOffset, borderRadius: element.style.borderRadius, transition: element.style.transition };
  element.style.transition = "outline-color 600ms ease";
  element.style.outline = "2px solid #2563eb";
  element.style.outlineOffset = "4px";
  element.style.borderRadius = element.style.borderRadius || "6px";
  window.setTimeout(() => {
    element.style.outline = previous.outline;
    element.style.outlineOffset = previous.outlineOffset;
    element.style.borderRadius = previous.borderRadius;
    element.style.transition = previous.transition;
  }, HIGHLIGHT_MS);
  return true;
}

export default function useFieldFocus() {
  const { hash, key } = useLocation();
  useEffect(() => {
    const id = (hash || "").replace(/^#/, "");
    if (!id.startsWith(FIELD_FOCUS_PREFIX)) return undefined;
    let cancelled = false;
    const started = Date.now();
    let timer;
    const attempt = () => {
      if (cancelled) return;
      if (focusField(id) || Date.now() - started > WAIT_MS) return;
      timer = window.setTimeout(attempt, POLL_MS);
    };
    timer = window.setTimeout(attempt, 0);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [hash, key]);
}
